import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  useTravelerSelection,
  ContactAndTravelerSelectors,
} from "@apac/checkout";
import { TravelerEditMode } from "@apac/travelers";
import { ProductTypeFunnels } from "@b2bportal/core-packages";
import { useModuleBEM } from "@b2bportal/core-themes";
import { Funnel, ProductType } from "@b2bportal/core-types";
import { Button, ButtonTypeEnum } from "@b2bportal/core-ui";
import { useDeviceType } from "@b2bportal/core-utilities";
import { useCheckoutStateSelector } from "@hopper-b2b/checkout";
import { useI18nContext } from "@hopper-b2b/i18n";
import { Dialog, DialogContent, Loading } from "@tenant/ui";
import { MultiSelectTravelerList } from "./MultiSelectTravelerList";
import styles from "./styles.module.scss";
import { TravelerList } from "./TravelerList";
import {
  getTravellerFormSubtitle,
  getTravellerFormTitle,
} from "@/modules/checkout";
import { useFlightTravelerLoading } from "@/modules/flight-checkout/hooks";
import { TravellerForm } from "@/overrides/apac/apac-travelers";
import { PackageSearchSelectors } from "@/store/packages/selectors";
import { TravelerRawEventName, useEmitRbcRawEvent } from "@/tracking";
import { CANADA_DATE_FORMAT } from "@/utilities";

/** See ActivitiesTravelerSelection for ACTIVITIES_ONLY */
const productTypeEditModeMap = {
  [ProductType.FLIGHTS]: TravelerEditMode.FLIGHTS_ONLY,
  [ProductType.CARS]: TravelerEditMode.CARS_ONLY,
  [ProductType.LODGING]: TravelerEditMode.HOTELS_ONLY,
  [ProductType.PACKAGE]: TravelerEditMode.ALL,
};

export const TravelerSelection = ({
  productType,
}: {
  productType: ProductType;
}) => {
  const [block, cn] = useModuleBEM(styles, "TravelerSelection");

  const singleTravelerWorkflow =
    productType === ProductType.LODGING || productType === ProductType.CARS;

  const isMissingPassportInfo = useCheckoutStateSelector(
    ContactAndTravelerSelectors.getIsPassportError
  );

  const resetErrors = !isMissingPassportInfo;

  const emitChooseTraveler = useEmitRbcRawEvent(
    TravelerRawEventName.ChooseTraveler
  );

  const {
    context: {
      travelerList,
      selectedTravelersIncludingInfants: selectedTravelers,
      isLoading,
      openTravelerFormModal,
      currentTraveler,
      disabled,
    },
    handlers: {
      handleSelectTraveler: originalHandleSelectTraveler,
      handleEditClick,
      handleDeleteTraveler,
      handleClickAddTraveler,
      handleCloseForm,
      handleUpdateTraveler,
    },
  } = useTravelerSelection(productType, singleTravelerWorkflow, resetErrors);

  const handleSelectTraveler = useCallback(
    (travelerId: string) => {
      originalHandleSelectTraveler(travelerId);

      emitChooseTraveler({
        properties: {
          product_type: productType,
        },
      });
    },
    [originalHandleSelectTraveler, productType, emitChooseTraveler]
  );

  // For flights, include the 'validating' state in loading check to prevent
  // users from proceeding while additional points purchase eligibility is being checked
  const isFlightTravelerLoading = useFlightTravelerLoading();
  const showLoader = useMemo(() => {
    const isFlightCheckout = productType === ProductType.FLIGHTS;
    return isFlightCheckout ? isFlightTravelerLoading : isLoading;
  }, [productType, isFlightTravelerLoading, isLoading]);

  const selectedTraveler = selectedTravelers?.[0];

  // Only show missing driver info error for standalone CARS, not packages
  const isMissingDriverInfo =
    productType === ProductType.CARS && !currentTraveler?.groundTravelerDetails;

  const { isDesktopAndUp } = useDeviceType();

  const { t } = useI18nContext();

  // Get package type to determine which fields to show for packages
  const packageType = useSelector(
    PackageSearchSelectors.getSearchFormPackageType
  );

  // For packages, always show all fields but make them optional (only driver needs DL)
  // For standalone products, use the standard edit modes
  const travelerEditMode =
    productType === ProductType.PACKAGE
      ? TravelerEditMode.ALL
      : productTypeEditModeMap[productType];

  // For packages, make DL fields optional since only the driver needs a license
  const driverLicenseFieldOverrides =
    productType === ProductType.PACKAGE
      ? {
          address: {
            label: t(
              "checkout.travelerInformation.travelerForm.driverLicense.address"
            ),
            placeholder: t(
              "checkout.travelerInformation.travelerForm.driverLicense.address"
            ),
            required: false,
          },
          issuingCity: {
            label: t(
              "checkout.travelerInformation.travelerForm.driverLicense.city"
            ),
            placeholder: t(
              "checkout.travelerInformation.travelerForm.driverLicense.city"
            ),
            required: false,
          },
          issuingCountry: {
            label: t(
              "checkout.travelerInformation.travelerForm.driverLicense.country"
            ),
            placeholder: t("choose"),
            required: false,
          },
          postalCode: {
            label: t(
              "checkout.travelerInformation.travelerForm.driverLicense.zipCode"
            ),
            placeholder: t(
              "checkout.travelerInformation.travelerForm.driverLicense.zipCodePlaceholder"
            ),
            required: false,
          },
        }
      : undefined;

  // Only require driver's license for standalone car bookings
  // Packages with cars do NOT require driver's license
  const showDriverLicenseForProductTypes = productType === ProductType.CARS;

  // Show passport details for flights or packages with flights (Air funnel)
  const showPassportDetailsForProductTypes =
    productType === ProductType.FLIGHTS ||
    (productType === ProductType.PACKAGE &&
      packageType !== undefined &&
      ProductTypeFunnels[packageType]?.includes(Funnel.Air));

  const handleSubmitTraveler = (
    traveler: Parameters<typeof handleUpdateTraveler>[0]
  ) => {
    handleUpdateTraveler(traveler);
  };

  const handleTravelerCloseForm = useCallback(() => {
    const isSelected = selectedTravelers?.some(
      (t) => t.id === currentTraveler?.id
    );

    if (isSelected && currentTraveler?.id) {
      let hasMissingDriverLicense = false;
      let hasMissingPassport = false;

      // Check for missing driver's license (required for CARS)
      if (showDriverLicenseForProductTypes) {
        hasMissingDriverLicense =
          !currentTraveler.groundTravelerDetails?.driverLicense;
      }

      // Check for missing passport (response from validate passenger endpoint)
      if (showPassportDetailsForProductTypes) {
        hasMissingPassport = isMissingPassportInfo;
      }

      // If still missing required info, unselect the traveler
      if (hasMissingDriverLicense || hasMissingPassport) {
        handleSelectTraveler(currentTraveler.id);
      }
    }

    handleCloseForm();
  }, [
    selectedTravelers,
    currentTraveler?.id,
    currentTraveler?.groundTravelerDetails?.driverLicense,
    handleCloseForm,
    showDriverLicenseForProductTypes,
    showPassportDetailsForProductTypes,
    isMissingPassportInfo,
    handleSelectTraveler,
  ]);

  const isMultiSelect =
    productType === ProductType.FLIGHTS || productType === ProductType.PACKAGE;
  const title = isMultiSelect
    ? t("checkout.travelerInformation.traveler.multiTravelerTitle")
    : t("checkout.travelerInformation.traveler.singleTravelerTitle");
  const subtitle = isMultiSelect
    ? t("checkout.travelerInformation.traveler.multiTravelerSubtitle")
    : t("checkout.travelerInformation.traveler.singleTravelerSubtitle");

  return (
    <div className={block}>
      <div className={cn("header")}>
        <h3 className={cn("title")}>{title}</h3>
        <p className={cn("subtitle")}>{subtitle}</p>
      </div>
      {showLoader ? (
        <div className={cn("component-loading")}>
          <Loading />
        </div>
      ) : (
        <>
          {isMultiSelect ? (
            <MultiSelectTravelerList
              travelerList={disabled ? selectedTravelers : travelerList}
              handleSelectTraveler={handleSelectTraveler}
              handleEdit={handleEditClick}
              handleDelete={handleDeleteTraveler}
              selectedTravelers={selectedTravelers}
              disabled={disabled}
            />
          ) : (
            <TravelerList
              travelerList={
                disabled && selectedTraveler ? [selectedTraveler] : travelerList
              }
              handleSelectTraveler={handleSelectTraveler}
              handleEdit={handleEditClick}
              handleDelete={handleDeleteTraveler}
              selectedTravelerId={selectedTraveler?.id}
              disabled={disabled}
            />
          )}
          {!disabled && (
            <Button
              className={cn("add-passenger")}
              onClick={() => handleClickAddTraveler()}
              buttonType={ButtonTypeEnum.Secondary}
            >
              {t("checkout.travelerInformation.traveler.button")}
            </Button>
          )}

          <Dialog
            open={openTravelerFormModal}
            onClose={handleTravelerCloseForm}
          >
            <DialogContent
              fullScreen={!isDesktopAndUp}
              title={getTravellerFormTitle(currentTraveler, travelerList, t)}
              className={cn("passenger-form-container")}
            >
              <p className={cn("modal-subtitle")}>
                {getTravellerFormSubtitle(productType, t)}
              </p>
              <TravellerForm
                onClose={handleTravelerCloseForm}
                traveler={currentTraveler}
                onDelete={handleDeleteTraveler}
                onSubmit={handleSubmitTraveler}
                travelerEditMode={travelerEditMode}
                nameFieldsOrder={["lastName", "firstName"]}
                closeButtonText={t("cancel")}
                showDriverLicenseDetails={{
                  showAddress: showDriverLicenseForProductTypes,
                  showCity: showDriverLicenseForProductTypes,
                }}
                fieldOverrides={
                  driverLicenseFieldOverrides
                    ? { driverLicense: driverLicenseFieldOverrides }
                    : undefined
                }
                requireDriverLicense={showDriverLicenseForProductTypes}
                missingDriverInfoError={isMissingDriverInfo}
                missingPassportInfoError={isMissingPassportInfo}
                showPassportDetails={showPassportDetailsForProductTypes}
                dateFormat={CANADA_DATE_FORMAT}
              />
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
};
